import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Radio, Select, TimePicker, DatePicker, Checkbox, Tag } from '@maxtropy/components';
import { Space } from 'antd';
import { apiV2PeriodDetailSavePost } from '@maxtropy/device-customer-apis-v2';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { DayPeriodItem, format, TimeType, weekOptions } from '../utils';
import { useParams } from 'react-router-dom';
import Month from './Month';
import dayjs from 'dayjs';

interface Props {
  currentMonth: number;
  monthPeriodList?: DayPeriodItem[];
  modalVisible?: boolean;
  setModalVisible: (v: boolean) => void;
  forceUpdate?: () => void;
}

const isConflictWithExisting = (
  monthPeriodList: DayPeriodItem[],
  index: number,
  startTime: dayjs.Dayjs,
  endTime: dayjs.Dayjs
): boolean => {
  if (!startTime || !endTime) return false;

  const adjustedEnd = endTime.format('HH:mm') === '00:00' ? endTime.add(1, 'day') : endTime;

  for (const item of monthPeriodList) {
    const currentDay = dayjs(item.currentDay);

    // 判断是否匹配 index（按周或按月）
    const isSameIndex = currentDay.date() === index || currentDay.day() === index;

    if (!isSameIndex) continue;

    for (const period of item.timeList || []) {
      const existStart = dayjs(period.startTime, 'HH:mm');
      let existEnd = dayjs(period.endTime, 'HH:mm');
      if (period.endTime === '00:00') {
        existEnd = existEnd.add(1, 'day');
      }

      const isOverlap = startTime.isBefore(existEnd) && adjustedEnd.isAfter(existStart);

      if (isOverlap) {
        return true;
      }
    }
  }

  return false;
};

const FastAddPeriodModal: React.FC<Props> = ({ currentMonth, modalVisible, setModalVisible, forceUpdate }) => {
  const [form] = Form.useForm();
  const [groupMonth, setGroupMonth] = React.useState<number[]>();
  const ruleEnum = Form.useWatch('ruleEnum', form);
  const { id } = useParams();
  const dateOptions = useMemo(() => {
    if (ruleEnum === TimeType.WEEK) {
      return weekOptions;
    }
    // 获取当前月份，根据当前月份渲染
    const days = dayjs(currentMonth).daysInMonth();
    return new Array(days).fill(0).map((_, index) => ({
      label: `${index + 1}号`,
      value: index + 1,
    }));
  }, [ruleEnum, currentMonth]);

  const onOk = () => {
    if (!id) return;
    form.validateFields().then(values => {
      Modal.confirm({
        title: '当前操作会覆盖已配置的时段数据，确认是否覆盖？',
        okText: '确定',
        onOk() {
          apiV2PeriodDetailSavePost({
            dataQueryPeriodId: id,
            currentMonth: groupMonth,
            ruleEnum: values.ruleEnum,
            ruleData: values.ruleData.map((item: any) => ({
              startIndex: item.dayIndex,
              endIndex: item.dayIndex,
              startTime: dayjs(item.startTime).format('HH:mm'),
              endTime: dayjs(item.endTime).format('HH:mm'),
            })),
          }).then(res => {
            setModalVisible(false);
            forceUpdate?.();
          });
        },
      });
    });
  };

  const onCancel = () => {
    form.resetFields();
    setModalVisible?.(false);
  };

  const handleMonth = (v: string[]) => {
    const timestampList = v.map(dateStr => {
      return dayjs(dateStr).startOf('month').valueOf();
    });
    setGroupMonth(timestampList);
  };
  return (
    <Modal
      contentClassName="modal-form-content"
      open={modalVisible}
      width={780}
      bodyScroll
      title="快捷新增时段"
      onCancel={onCancel}
      onOk={onOk}
      destroyOnClose
    >
      <Form
        form={form}
        initialValues={{
          ruleEnum: TimeType.WEEK,
          ruleData: [
            {
              dayIndex: undefined,
              startTime: undefined,
              endTime: undefined,
            },
          ],
          year: null,
          months: [],
        }}
      >
        <Form.Item name="months" label="时间范围" rules={[{ required: true }]}>
          <Month onChange={handleMonth} />
        </Form.Item>
        <Form.Item label="时间类型" name="ruleEnum" rules={[{ required: true }]}>
          <Radio.Group
            onChange={() => {
              form.setFieldValue('ruleData', [
                {
                  dayIndex: undefined,
                  startTime: undefined,
                  endTime: undefined,
                },
              ]);
            }}
          >
            <Radio value={TimeType.WEEK}>周</Radio>
            <Radio value={TimeType.MONTH}>月</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="时间" required>
          <Form.List name="ruleData">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }, index) => (
                  <div className={styles.newlayout} key={index}>
                    <div
                      style={{
                        display: 'flex',
                        columnGap: 8,
                      }}
                    >
                      <Form.Item name={[name, 'dayIndex']} rules={[{ required: true, message: '请选择' }]}>
                        <Select style={{ width: 100 }} placeholder="请选择" options={dateOptions} />
                      </Form.Item>

                      <Form.Item
                        name={[name, 'startTime']}
                        rules={[
                          { required: true, message: '请选择开始时间' },
                          {
                            validator: async (_, value) => {
                              const ruleData = form.getFieldValue('ruleData') || [];
                              const current = ruleData[index];
                              const { dayIndex, endTime } = current || {};
                              if (!value || !endTime || !dayIndex) return Promise.resolve();

                              const start = dayjs(value, 'HH:mm');
                              const end = dayjs(endTime, 'HH:mm');
                              const adjustedEnd = end.format('HH:mm') === '00:00' ? end.add(1, 'day') : end;

                              if (!start.isBefore(adjustedEnd)) {
                                return Promise.reject(new Error('开始时间必须早于结束时间'));
                              }

                              // 校验与已有数据冲突
                              // if (isConflictWithExisting(monthPeriodList, dayIndex, start, adjustedEnd)) {
                              //   return Promise.reject(new Error('时段与现有时段冲突'));
                              // }

                              // 校验与当前新增项中的其他项是否冲突
                              for (let i = 0; i < ruleData.length; i++) {
                                if (i === index) continue;
                                const other = ruleData[i];
                                if (!other || other.dayIndex !== dayIndex || !other.startTime || !other.endTime)
                                  continue;

                                const otherStart = dayjs(other.startTime, 'HH:mm');
                                const otherEndRaw = dayjs(other.endTime, 'HH:mm');
                                const otherEnd = other.endTime === '00:00' ? otherEndRaw.add(1, 'day') : otherEndRaw;

                                const isOverlap = start.isBefore(otherEnd) && adjustedEnd.isAfter(otherStart);
                                if (isOverlap) {
                                  return Promise.reject(new Error('与其他新增时段冲突'));
                                }
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <TimePicker placeholder="请选择" format={format} showNow={false} />
                      </Form.Item>
                      <div>-</div>
                      <Form.Item
                        name={[name, 'endTime']}
                        rules={[
                          { required: true, message: '请选择结束时间' },
                          {
                            validator: async (_, value) => {
                              const ruleData = form.getFieldValue('ruleData') || [];
                              const current = ruleData[index];
                              const { dayIndex, startTime } = current || {};
                              if (!value || !startTime || !dayIndex) return Promise.resolve();

                              const start = dayjs(startTime, 'HH:mm');
                              const end = dayjs(value, 'HH:mm');
                              const adjustedEnd = end.format('HH:mm') === '00:00' ? end.add(1, 'day') : end;

                              if (!start.isBefore(adjustedEnd)) {
                                return Promise.reject(new Error('结束时间必须晚于开始时间'));
                              }

                              // 与已有时段冲突判断
                              // if (isConflictWithExisting(monthPeriodList, dayIndex, start, adjustedEnd)) {
                              //   return Promise.reject(new Error('时段与现有时段冲突'));
                              // }

                              // 与当前表单中其他项冲突判断
                              for (let i = 0; i < ruleData.length; i++) {
                                if (i === index) continue;
                                const other = ruleData[i];
                                if (!other || other.dayIndex !== dayIndex || !other.startTime || !other.endTime)
                                  continue;

                                const otherStart = dayjs(other.startTime, 'HH:mm');
                                const otherEndRaw = dayjs(other.endTime, 'HH:mm');
                                const otherEnd = other.endTime === '00:00' ? otherEndRaw.add(1, 'day') : otherEndRaw;

                                const isOverlap = start.isBefore(otherEnd) && adjustedEnd.isAfter(otherStart);
                                if (isOverlap) {
                                  return Promise.reject(new Error('与其他新增时段冲突'));
                                }
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <TimePicker placeholder="请选择" format={format} showNow={false} />
                      </Form.Item>
                      <MinusCircleOutlined style={{ fontSize: 16, color: '#4A90E2' }} onClick={() => remove(name)} />
                    </div>
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    wrapStyle={{ width: 380 }}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    className={styles.btn}
                    ghost
                  >
                    新增时段
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FastAddPeriodModal;
